﻿import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  TrendingUp,
  DollarSign,
  ShieldCheck,
  LayoutDashboard, Loader2, AlertCircle, FolderOpen,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { formatCurrency } from "../../../owner/data/ownerPrototype";
import type { Identifier } from "../../../pages/ProjectManagement/types";
import type { ProviderManproProjectDto } from "../../../api/manpro";
import ProjectWorkspaceOverview from "./ProjectWorkspaceOverview";
import ProjectDetailsSkeleton from "./ProjectDetailsSkeleton";
import ProjectManagementTabs from "./ProjectManagementTabs";
import ProjectManagementFinance from "./ProjectManagementFinance";
import PlanningSection from "./PlanningSection";
import ExecutionSection from "./ExecutionSection";
import ValidationSection from "./ValidationSection";
import ReportingSection from "./ReportingSection";
import { TeamManagementPage } from "../../../components/teamManagement/TeamManagementPage";
import type { BaselineData, ExecutionData } from "../../../pages/ProjectManagement/types";

// Helper: map status to color config
const getProjectStatusConfig = (status?: string) => {
  const s = String(status || "").toLowerCase();
  switch (s) {
    case "active":
    case "in_progress":
    case "execution":
    case "converted_to_manpro":
      return { label: "Berjalan", bg: "bg-blue-50", text: "text-blue-700" };
    case "completed":
      return { label: "Selesai", bg: "bg-emerald-50", text: "text-emerald-700" };
    case "pending_owner_approval":
      return { label: "Menunggu Persetujuan", bg: "bg-amber-50", text: "text-amber-700" };
    case "pending_rab_approval":
      return { label: "Menunggu Persetujuan RAB", bg: "bg-amber-50", text: "text-amber-700" };
    case "pending_bast_approval":
      return { label: "Menunggu Persetujuan BAST", bg: "bg-amber-50", text: "text-amber-700" };
    case "on_hold":
      return { label: "Ditunda", bg: "bg-red-50", text: "text-red-700" };
    case "draft":
      return { label: "Draft", bg: "bg-slate-50", text: "text-slate-700" };
    case "proposal_review":
    case "open_for_bid":
      return { label: "Tahap Tender", bg: "bg-purple-50", text: "text-purple-700" };
    default:
      return { label: status || "Unknown", bg: "bg-slate-50", text: "text-slate-700" };
  }
};

type ProviderProjectWorkspaceProps = {
  providerManproProjects: ProviderManproProjectDto[];
  providerManproProjectsQuery: { isLoading: boolean; isError: boolean };
  selectedProviderProject: ProviderManproProjectDto | null;
  activeProjectId: Identifier | null;
  activeProjectData: any | null;
  isWorkspaceOpen: boolean;
  isProjectLoading: boolean;
  // Workspace overview props
  baselineData: BaselineData;
  executionData: ExecutionData;
  workspaceTitle: string;
  workspaceStatus: string;
  nextAction: string;
  progressAverage: number;
  milestoneCount: number;
  taskCount: number;
  isRejectedProject: boolean;
  isPendingApproval: boolean;
  activeTab: string;
  // Actions
  onNewProject: () => void;
  onOpenProject: (project: ProviderManproProjectDto) => void;
  onSelectProject: (id: Identifier) => void;
  onCloseWorkspace: () => void;
  onShareLink: () => void;
};

const ProviderProjectWorkspace = ({
  providerManproProjects,
  providerManproProjectsQuery,
  selectedProviderProject,
  activeProjectId,
  activeProjectData,
  isWorkspaceOpen,
  isProjectLoading,
  baselineData,
  executionData,
  workspaceTitle,
  workspaceStatus,
  nextAction,
  progressAverage,
  milestoneCount,
  taskCount,
  isRejectedProject,
  isPendingApproval,
  activeTab,
  onNewProject,
  onOpenProject,
  onSelectProject,
  onCloseWorkspace,
  onShareLink,
}: ProviderProjectWorkspaceProps) => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("Semua");

  const filterOptions = ["Semua", "Berjalan", "Tahap Tender", "Menunggu Persetujuan", "Selesai", "Ditunda", "Draft"];

  const filteredProjects = useMemo(() => {
    if (statusFilter === "Semua") return providerManproProjects;
    return providerManproProjects.filter((project) => {
      const config = getProjectStatusConfig(project.status || undefined);
      return config.label === statusFilter;
    });
  }, [providerManproProjects, statusFilter]);

  // Computed metrics
  const totalProjects = providerManproProjects.length;
  const activeProjects = providerManproProjects.filter((project) =>
    ["active", "execution", "in_progress", "converted_to_manpro"].includes(
      String(project.status || "").toLowerCase(),
    ),
  ).length;
  const totalBudget = providerManproProjects.reduce(
    (sum, project) =>
      sum + Number((project as any).budget || (project as any).owner_project?.budget_max || 0),
    0,
  );
  const averageProgress =
    totalProjects > 0
      ? Math.round(
          providerManproProjects.reduce(
            (sum, project) => sum + Number((project as any).progress || 0),
            0,
          ) / totalProjects,
        )
      : 0;

  // If workspace is open (project detail view)
  if (isWorkspaceOpen && selectedProviderProject) {
    return (
      <div className="space-y-6">
        <ProjectWorkspaceOverview
          baselineData={baselineData}
          activeProjectId={activeProjectId}
          activeProjectData={activeProjectData ?? selectedProviderProject}
          workspaceTitle={workspaceTitle}
          workspaceStatus={workspaceStatus}
          nextAction={nextAction}
          progressAverage={progressAverage}
          milestoneCount={milestoneCount}
          taskCount={taskCount}
          isRejectedProject={isRejectedProject}
          isPendingApproval={isPendingApproval}
          onClose={onCloseWorkspace}
          onShareLink={onShareLink}
        />
        <div className="space-y-5">
          {isProjectLoading ? (
            <ProjectDetailsSkeleton />
          ) : (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-[0_10px_30px_rgba(0,0,0,0.05)]">
                {activeTab === "planning" && <PlanningSection />}
                {activeTab === "execution" && <ExecutionSection />}
                {activeTab === "validation" && <ValidationSection />}
                {activeTab === "reporting" && <ReportingSection />}
                {activeTab === "team" && (
                  <TeamManagementPage
                    projectId={String(activeProjectId || "1")}
                    projectName={baselineData.projectName || "Proyek MANPRO"}
                  />
                )}
                {activeTab === "finance" && <ProjectManagementFinance />}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Landing view for provider (project list)
  return (
  <div className="space-y-6">
    {/* Hero Banner */}
    <div className="rounded-2xl sm:rounded-3xl bg-gradient-to-br from-blue-700 via-blue-600 to-slate-950 p-5 sm:p-6 md:p-8 text-white shadow-sm">
      <div className="flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.2em] sm:tracking-[0.25em] text-blue-200">
            Workspace Kelola Proyek
          </p>
          <h1 className="mt-2 sm:mt-3 text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
            Workspace Kelola Proyek
          </h1>
          <p className="mt-2 sm:mt-3 text-xs sm:text-sm leading-relaxed text-blue-100">
            Workspace ini berisi project dari Bid Owner dan Proyek Pribadi milik provider yang
            sedang login. Fokusnya perencanaan, resource, pelaksanaan, dan monitoring.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2.5 sm:gap-3 w-full lg:w-auto align-items-center justify-start sm:justify-end mt-4 sm:mt-0">
          <button
            type="button"
            onClick={() => navigate("/provider-opportunities")}
            className="w-full sm:w-auto justify-center rounded-xl bg-white/10 px-4 py-2.5 text-center text-sm font-bold text-white ring-1 ring-white/25 transition hover:bg-white/15 active:scale-95"
          >
            Cari Peluang
          </button>
          <button
            type="button"
            onClick={() => navigate("/provider-bids")}
            className="w-full sm:w-auto justify-center rounded-xl bg-white px-4 py-2.5 text-center text-sm font-bold text-blue-700 transition hover:bg-blue-50 active:scale-95"
          >
            Penawaran Saya
          </button>
          <button
            type="button"
            onClick={onNewProject}
            className="inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-600 active:scale-95"
          >
            <LayoutDashboard size={17} />
            <span>Buat Proyek Pribadi</span>
          </button>
        </div>
      </div>
    </div>

    {/* Stats Grid */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
      {(
        [
          ["Kelola Proyek", totalProjects, Briefcase, "bg-blue-50 text-blue-700"],
          ["Project Aktif", activeProjects, ShieldCheck, "bg-emerald-50 text-emerald-700"],
          ["Rata-rata Progress", `${averageProgress}%`, TrendingUp, "bg-purple-50 text-purple-700"],
          ["Nilai Delivery", formatCurrency(totalBudget), DollarSign, "bg-amber-50 text-amber-700"],
        ] satisfies Array<[string, string | number, LucideIcon, string]>
      ).map(([label, value, Icon, tone]) => (
        <div key={String(label)} className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 shadow-sm transition hover:shadow-md">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-extrabold text-slate-700">{String(label)}</p>
              <p className="mt-2 sm:mt-3 truncate text-xl sm:text-2xl font-extrabold text-slate-900">{String(value)}</p>
            </div>
            <div className={`grid h-10 w-10 sm:h-12 sm:w-12 shrink-0 place-items-center rounded-xl sm:rounded-full ${String(tone)}`}>
              <Icon size={20} className="sm:h-5 sm:w-5" />
            </div>
          </div>
        </div>
      ))}
    </div>

    {/* Project list */}
    {providerManproProjectsQuery.isLoading ? (
      <div className="flex flex-col items-center justify-center rounded-2xl sm:rounded-3xl border border-slate-200 bg-white p-12 sm:p-16 text-center shadow-sm">
        <Loader2 className="h-10 w-10 animate-spin text-blue-500" />
        <p className="mt-4 text-sm font-semibold text-slate-600">Memuat data kelola proyek...</p>
      </div>
    ) : providerManproProjectsQuery.isError ? (
      <div className="flex flex-col items-center justify-center rounded-2xl sm:rounded-3xl border border-red-200 bg-red-50 p-12 sm:p-16 text-center shadow-sm">
        <div className="rounded-full bg-red-100 p-3 text-red-600 mb-4">
          <AlertCircle size={28} />
        </div>
        <h3 className="text-base font-bold text-red-700">Gagal Memuat Data</h3>
        <p className="mt-2 text-sm font-medium text-red-600 max-w-md">Terjadi kesalahan saat mengambil data proyek kelola. Silakan coba muat ulang halaman ini.</p>
      </div>
    ) : totalProjects === 0 ? (
      <div className="flex flex-col items-center justify-center rounded-2xl sm:rounded-3xl border border-dashed border-slate-300 bg-white p-12 sm:p-16 text-center shadow-sm">
        <div className="rounded-full bg-slate-100 p-4 text-slate-400 mb-4">
          <FolderOpen size={32} />
        </div>
        <h2 className="text-lg sm:text-xl font-extrabold text-slate-900">Belum ada kelola proyek aktif</h2>
        <p className="mt-2 max-w-xl text-xs sm:text-sm leading-relaxed text-slate-500">
          Project akan muncul otomatis di sini setelah Owner menerima bid provider dan backend membuat <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-xs">manpro_project_id</code>.
        </p>
        <button
          type="button"
          onClick={() => navigate("/provider-bids")}
          className="mt-6 rounded-xl bg-blue-600 px-6 py-2.5 sm:py-3 text-sm font-extrabold text-white transition hover:bg-blue-700 active:scale-95"
        >
          Cek Status Penawaran
        </button>
      </div>
    ) : (
      <div className="space-y-6">
        {/* Tabs Filter */}
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {filterOptions.map((opt) => (
            <button
              key={opt}
              onClick={() => setStatusFilter(opt)}
              className={`rounded-full px-4 py-1.5 text-xs sm:text-sm font-semibold transition-colors ${
                statusFilter === opt
                  ? "bg-blue-600 text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              {opt}
            </button>
          ))}
        </div>

        <section>
          {/* Ubah ke 2 kolom di layar besar (lg:grid-cols-2) agar layout lebih padat dan rapi */}
          {filteredProjects.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">
              <FolderOpen className="h-10 w-10 text-slate-400 mb-3" />
              <h3 className="text-sm font-bold text-slate-700">Data Tidak Ada</h3>
              <p className="text-xs text-slate-500 mt-1">Tidak ada proyek dengan status "{statusFilter}".</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredProjects.map((project) => {
              const sourceProject =
                (project as any).owner_project || (project as any).ownerProject;
              const isSelected =
                String(project.id) === String(selectedProviderProject?.id);

              return (
                <button
                  key={String(project.id)}
                  type="button"
                  onClick={() => {
                    onSelectProject(project.id);
                    onOpenProject(project);
                  }}
                  className={`flex flex-col justify-between w-full rounded-2xl border p-4 sm:p-5 text-left transition ${
                    isSelected
                      ? "border-blue-300 bg-blue-50/70 shadow-sm ring-2 ring-blue-500/20"
                      : "border-slate-200 bg-white hover:border-blue-200 hover:shadow-sm"
                  }`}
                >
                  <div className="w-full">
                    <div className="flex items-start justify-between gap-2">
                      <span
                        className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-bold ${
                          sourceProject
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-purple-50 text-purple-700"
                        }`}
                      >
                        {sourceProject ? "Masuk Kelola Proyek" : "Proyek Pribadi"}
                      </span>
                      {(() => {
                        const statusConfig = getProjectStatusConfig(
                          (project as any).status || "unknown status",
                        );
                        return (
                          <span
                            className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-bold ${statusConfig.bg} ${statusConfig.text}`}
                          >
                            {statusConfig.label}
                          </span>
                        );
                      })()}
                    </div>

                    <h3 className="mt-3 text-base sm:text-lg font-extrabold text-slate-900 line-clamp-1">
                      {sourceProject?.title ||
                        (project as any).name ||
                        `Project #${project.id}`}
                    </h3>
                    <p className="mt-1 text-xs sm:text-sm font-medium text-slate-500 line-clamp-1">
                      {sourceProject?.location ||
                        sourceProject?.city ||
                        "Proyek Internal Provider"}
                    </p>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs font-semibold text-slate-600 w-full">
                    <div className="truncate">
                      <span className="text-slate-400">Deadline: </span>
                      {(project as any).deadline_date ||
                        sourceProject?.estimated_finish ||
                        "-"}
                    </div>
                    <div className="truncate sm:text-right">
                      <span className="text-slate-400">Budget: </span>
                      <span className="font-bold text-slate-800">
                        {formatCurrency(
                          Number(
                            (project as any).budget || sourceProject?.budget_max || 0,
                          ),
                        )}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
          )}
        </section>
      </div>
    )}
  </div>
);
};

export default ProviderProjectWorkspace;